import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiResponse } from "./loginApis";
import { fetch3, fetch5, fetch8 } from "../commonApis/commonApis";
const url = process.env.REACT_APP_BASE_URL;

type NotificationStatus = {
    status: string;
}

export const useNotificationCreateMutation = () => {
    return useMutation({
        mutationFn: async (payload: any) => {
            const res: ApiResponse = await fetch8(`${url}/api/lm-notifications`, 'POST', payload);

            // Manually throw error if success is false
            if (!res.success) {
                throw res.message || 'Something went wrong';
            }

            return res;
        },
        onSuccess: (res: any) => {
            console.log('✅ Notification Created successfully:', res);
            return res;
        },
        onError: (error: any) => {
            console.error('❌ Notification create request failed:', error.message);
        }
    });
};

export const useFetchNotificationsData = (page: number, limit: number, searchFor: string) => {
    return useQuery({
        queryKey: ["NotificationsData", page, limit, searchFor],
        queryFn: async () => {
            const res = await fetch3(
                `${url}/api/lm-notifications?page=${page}&limit=${limit}&searchFor=${encodeURIComponent(searchFor)}`,
                "get"
            );

            if (!res || !res.data) {
                return res.message;
            }

            return res.data;
        },
        staleTime: 1000 * 60 * 5,
    });
};

export const useFetchAdminNotificationsData = (page: number,limit:number, searchFor: string) => {
    return useQuery({
        queryKey: ["AdminNotificationsData"],
        queryFn: async () => {
            const res = await fetch3(
                `${url}/api/admin-notifications?page=${page}&limit=${limit}&searchFor=${searchFor}`,
                "get"
            );

            if (!res || !res.data) {
                return res.message;
            }

            return res.data; // 👈 This returns your full user object
        },
        staleTime: 1000 * 60 * 5,
    });
};

export const useFetchNotificationDetailsData = (notificationId?: string) => {
    return useQuery({
        queryKey: ["notificationDetails", notificationId],
        queryFn: async () => {
            if (!notificationId) return null; // 👈 early exit inside the hook logic
            const res = await fetch3(`${url}/api/lm-notifications/${notificationId}?type=LM`, "get");

            if (!res || !res.data) return res.message;
            return res.data;
        },
        enabled: !!notificationId, // 👈 run only when id exists
        staleTime: 1000 * 60 * 5,
    });
};

export const useFetchNotificationBulkJobDetailsData = (bulkJobId?: string) => {
    return useQuery({
        queryKey: ["bulkJobNotificationDetails", bulkJobId],
        queryFn: async () => {
            if (!bulkJobId) return null;
            const res = await fetch3(`${url}/api/bulk/jobs/${bulkJobId}?type=LM`, "get");

            if (!res || !res.data) return res.message;
            return res.data;
        },
        enabled: !!bulkJobId,
        staleTime: 1000 * 60 * 5,
    });
};

export const useNotificationUpdateMutation = (id: string) => {
    return useMutation({
        mutationFn: async (payload: any) => {
            const res: ApiResponse = await fetch5(`${url}/api/lm-notifications/${id}`, "PUT", payload);

            // Manually throw error if success is false
            if (!res.success) {
                throw res.message || 'Something went wrong';
            }

            return res;
        },
        onSuccess: (data: any) => {
            console.log('✅ Notification updated successfully:', data);
            return data;
        },
        onError: (error: any) => {
            console.error('❌ Notification update request failed:', error.message);
        }
    });
};

export const useNotificationStatusMutation = (id: string) => {
    return useMutation({
        mutationFn: async (payload: NotificationStatus) => {
            const res: ApiResponse = await fetch5(
                `${url}/api/lm-notifications/${id}`,
                "PATCH",
                payload
            );

            // Manually throw error if success is false
            if (!res.success) {
                throw res.message || "Something went wrong";
            }

            return res;
        },
        onSuccess: (data: any) => {
            console.log("✅ Notification status updated successfully:", data);
            return;
        },
        onError: (error: any) => {
            console.error("❌ Notification status update request failed:", error.message);
        },
    });
};

export const useSeenAdminNotificationMutation = (id: string) => {
    return useMutation({
        mutationFn: async () => {
            const res: ApiResponse = await fetch5(
                `${url}/api/admin-notifications/${id}`,
                "PATCH",
                undefined
            );

            // Manually throw error if success is false
            if (!res.success) {
                throw res.message || "Something went wrong";
            }

            return res;
        },
        onSuccess: (data: any) => {
            console.log("✅ Notification seen updated successfully:", data);
            return;
        },
        onError: (error: any) => {
            console.error("❌ Notification seen update request failed:", error.message);
        },
    });
};